import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useUIStore } from '../../lib/stores/ui-store';
import { useRBAC } from '../../hooks/use-rbac';
import { type PermissionCategory } from '../../lib/config/permissions';
import { hasPermission, type UserRole, type NavCategory } from '../../lib/config/rbac-permissions';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  VisuallyHidden,
} from '../ui/sheet';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Cog,
  FileCheck,
  ClipboardCheck,
  FileSearch,
  Package,
  AlertTriangle,
  ArrowLeft,
  Search,
  Truck,
  Wrench,
} from 'lucide-react';
import { useUserStore } from '../../lib/stores/user-store';
import { useOrganizationStore } from '../../lib/stores/organization-store';
import { useState } from 'react';
import { useLowStockParts } from '../../lib/api/parts';
import { GlobalSearch } from '../common/global-search';
import { APP_NAME } from '../../lib/helpers';
import logo from '../../assets/logo1.png';
import { MOD_SYMBOL } from '../../lib/config/keyboard-shortcuts';

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
  badgeVariant?: 'default' | 'warning';
  shortcut?: string;
  carrierOnly?: boolean;
  brokerOnly?: boolean;
  permission?: PermissionCategory;
  isCreateAction?: boolean;
  children?: NavItem[];
}

// Top quick actions for maintenance layout
const getTopActions = (): NavItem[] => [
  { title: 'Go to Dashboard', href: '/', icon: ArrowLeft, permission: 'dashboard' },
  { title: 'Search', href: '/search', icon: Search, shortcut: `${MOD_SYMBOL}K`, permission: 'dashboard' },
];

// Maintenance-only navigation
const getMaintenanceNavigation = (lowStockCount = 0): NavItem[] => [
  {
    title: 'Maintenance',
    href: '/maintenance',
    icon: Cog,
    children: [
      { title: 'Work Orders', href: '/maintenance/work-orders', icon: ClipboardCheck, permission: 'workOrders' as PermissionCategory },
      { title: 'Equipment', href: '/maintenance/fleet', icon: Truck, permission: 'fleet' },
      { title: 'Scheduled Maintenance', href: '/maintenance', icon: FileCheck, permission: 'maintenance' },
      { title: 'Inventory', href: '/maintenance/parts', icon: Package, permission: 'parts' as PermissionCategory, badge: lowStockCount > 0 ? lowStockCount : undefined, badgeVariant: 'warning' as const },
      { title: 'Technicians', href: '/maintenance/technicians', icon: Wrench, permission: 'maintenance' },
      { title: 'Inspections', href: '/maintenance/inspections', icon: FileSearch, permission: 'maintenance' },
    ]
  },
];

// Shared navigation component used in both desktop sidebar and mobile sheet
function MaintenanceSidebarNav({
  topActions,
  filteredNavigation,
  pathname,
  sidebarCollapsed = false,
  onNavigate,
  onSearchClick,
}: {
  topActions: NavItem[];
  filteredNavigation: NavItem[];
  pathname: string;
  sidebarCollapsed?: boolean;
  onNavigate?: () => void;
  onSearchClick?: () => void;
}) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'Maintenance': true, // Auto-expand maintenance section
  });

  const toggleSection = (itemTitle: string, e?: React.MouseEvent) => {
    const isExpanding = !expandedSections[itemTitle];

    setExpandedSections(prev => ({
      ...prev,
      [itemTitle]: isExpanding
    }));

    if (isExpanding && e) {
      const parent = e.currentTarget.parentElement;
      if (parent) {
        setTimeout(() => {
          parent.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
    }
  };

  const renderNavItem = (item: NavItem, isChild = false) => {
    const isActive = pathname === item.href || (item.children?.some(child => child.href === pathname));
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedSections[item.title];
    const isSearchItem = item.title === 'Search';

    const handleItemClick = (e: React.MouseEvent) => {
      if (isSearchItem && onSearchClick) {
        e.preventDefault();
        onSearchClick();
        onNavigate?.();
      } else {
        onNavigate?.();
      }
    };

    if (sidebarCollapsed) {
      if (isSearchItem) {
        return (
          <Tooltip key={item.title}>
            <TooltipTrigger asChild>
              <button
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-md mx-auto',
                  'transition-colors hover:bg-blue-50'
                )}
                onClick={handleItemClick}
              >
                <Icon className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {item.title}
            </TooltipContent>
          </Tooltip>
        );
      }

      return (
        <Tooltip key={item.title}>
          <TooltipTrigger asChild>
            <Link
              to={item.href}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-md mx-auto',
                'transition-colors hover:bg-blue-50',
                isActive && 'bg-blue-100 text-blue-700'
              )}
              onClick={onNavigate}
            >
              <Icon className="h-4 w-4" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">
            {item.title}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <div key={item.title}>
        {hasChildren ? (
          <>
            <button
              onClick={(e) => toggleSection(item.title, e)}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm',
                'transition-colors hover:bg-blue-50',
                isActive && 'bg-blue-100 text-blue-700',
                isChild && 'pl-10'
              )}
            >
              {!isChild && <Icon className="h-4 w-4 flex-shrink-0" />}
              <span className="flex-1 text-left font-normal">{item.title}</span>
              {item.badge && (
                item.badgeVariant === 'warning' ? (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-600">
                    <AlertTriangle className="h-3 w-3" />
                    {item.badge}
                  </span>
                ) : (
                  <span className={cn(
                    'px-2 py-0.5 text-[10px] font-medium rounded',
                    item.badge === 'Try Demo'
                      ? 'bg-blue-100 text-blue-600'
                      : item.badge === 'New'
                        ? 'bg-orange-100 text-orange-600'
                        : item.badge === 'Beta'
                          ? 'bg-orange-100 text-orange-600'
                          : 'bg-primary/10 text-primary'
                  )}>
                    {item.badge}
                  </span>
                )
              )}
              {item.shortcut && (
                <span className="text-xs text-muted-foreground">{item.shortcut}</span>
              )}
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              )}
            </button>
            {isExpanded && item.children && (
              <div className="mt-1 space-y-0.5">
                {item.children.map(child => renderNavItem(child, true))}
              </div>
            )}
          </>
        ) : isSearchItem ? (
          <button
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm w-full',
              'transition-colors hover:bg-accent/50',
              isChild && 'pl-10 text-[13px]'
            )}
            onClick={handleItemClick}
          >
            {!isChild && <Icon className="h-4 w-4 flex-shrink-0" />}
            <span className="flex-1 font-normal text-left">{item.title}</span>
            {item.shortcut && (
              <span className="text-xs text-muted-foreground flex-shrink-0">{item.shortcut}</span>
            )}
          </button>
        ) : (
          <Link
            to={item.href}
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm',
              'transition-colors hover:bg-blue-50',
              isActive && 'bg-blue-100 text-blue-700',
              isChild && 'pl-10 text-[13px]'
            )}
            onClick={onNavigate}
          >
            {!isChild && <Icon className="h-4 w-4 flex-shrink-0" />}
            <span className="flex-1 font-normal">{item.title}</span>
            {item.badge && (
              item.badgeVariant === 'warning' ? (
                <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-600 flex-shrink-0">
                  <AlertTriangle className="h-3 w-3" />
                  {item.badge}
                </span>
              ) : (
                <span className={cn(
                  'px-2 py-0.5 text-[10px] font-medium rounded flex-shrink-0',
                  item.badge === 'Try Demo'
                    ? 'bg-blue-100 text-blue-600'
                    : item.badge === 'New'
                      ? 'bg-orange-100 text-orange-600'
                      : item.badge === 'Beta'
                        ? 'bg-orange-100 text-orange-600'
                        : 'bg-blue-100 text-blue-600'
                )}>
                  {item.badge}
                </span>
              )
            )}
            {item.shortcut && (
              <span className="text-xs text-muted-foreground flex-shrink-0">{item.shortcut}</span>
            )}
          </Link>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top Actions (Go to Dashboard, Search) */}
      <nav className="flex-shrink-0 flex flex-col gap-0.5 p-2">
        {topActions.map((item) => renderNavItem(item))}
      </nav>

      {/* Divider */}
      {!sidebarCollapsed && <Separator className="mx-2 flex-shrink-0" />}

      {/* Main Navigation - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <nav className="flex flex-col gap-0.5 p-2">
          {filteredNavigation.map((item) => renderNavItem(item))}
        </nav>
      </div>
    </div>
  );
}

export function MaintenanceSidebar({ isMobileWebView = false }: { isMobileWebView?: boolean } = {}) {
  const location = useLocation();
  const pathname = location.pathname;
  const { sidebarCollapsed, toggleSidebar, sidebarMobileOpen, setSidebarMobileOpen } = useUIStore();
  const { organization } = useOrganizationStore();
  const rbac = useRBAC();
  const { user } = useUserStore();
  const userRole = ((organization?.roles as string[])?.[0] || user?.role || 'driver') as UserRole;
  const [searchOpen, setSearchOpen] = useState(false);

  const topActions = isMobileWebView
    ? getTopActions().filter(item => item.title !== 'Go to Dashboard')
    : getTopActions();

  const { data: lowStockData } = useLowStockParts();

  const navigation = getMaintenanceNavigation(lowStockData?.count || 0);

  const handleSearchClick = () => {
    setSearchOpen(true);
  };

  // Filter navigation items and their children based on permissions
  const filterItems = (items: NavItem[]): NavItem[] => {
    return items.filter(item => {
      // Check business mode restrictions
      const businessMode = (organization?.businessMode || 'carrier') as 'carrier' | 'broker' | 'customer_portal';
      if (item.carrierOnly && businessMode !== 'carrier') return false;
      if (item.brokerOnly && businessMode !== 'broker') return false;

      // Check role-based permission using RBAC nav visibility
      if (item.permission) {
        if (item.isCreateAction) {
          const perm = hasPermission(userRole, item.permission, 'create');
          if (!perm || perm === 'view') return false;
        } else if (!rbac.canViewNav(item.permission as NavCategory)) {
          return false;
        }
      }

      // Filter children recursively
      if (item.children) {
        item.children = filterItems(item.children);
        if (item.children.length === 0) return false;
      }

      return true;
    });
  };

  // Filter navigation based on permissions
  const filteredTopActions = filterItems(topActions);
  const filteredNavigation = filterItems(navigation);

  // Get company initials for logo
  const companyInitial = organization?.name?.charAt(0)?.toUpperCase() || 'T';

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile/Tablet Sheet - Slides in from left */}
      <Sheet open={sidebarMobileOpen} onOpenChange={setSidebarMobileOpen}>
        <SheetContent
          side="left"
          className="w-64 p-0 min-[860px]:hidden"
        >
          <VisuallyHidden>
            <SheetTitle>Maintenance Menu</SheetTitle>
          </VisuallyHidden>
          <div className="flex h-14 items-center justify-between border-b px-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 shrink-0 truncate rounded bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm">
                {companyInitial}
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-sm leading-tight truncate">
                  {organization?.name || APP_NAME}
                </span>
                <span className="text-xs text-muted-foreground">
                  Maintenance
                </span>
              </div>
            </div>
          </div>
          <MaintenanceSidebarNav
            topActions={filteredTopActions}
            filteredNavigation={filteredNavigation}
            pathname={pathname}
            onNavigate={() => setSidebarMobileOpen(false)}
            onSearchClick={handleSearchClick}
          />
        </SheetContent>
      </Sheet>

      {/* Global Search Dialog */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Desktop Sidebar - hidden on mobile and tablet, shown on desktop */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen border-r bg-background transition-all duration-300',
          'hidden min-[860px]:flex min-[860px]:flex-col',
          sidebarCollapsed ? 'w-16' : 'w-70'
        )}
      >
        <div className={`flex h-14 items-center justify-between border-b ${sidebarCollapsed ? ' px-0' : ' px-4'}`}>
          {!sidebarCollapsed ? (
            <>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="rounded shrink-0 flex items-center justify-center text-primary-foreground font-semibold text-sm">
                  <img src={logo} className='w-20' alt="" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="font-semibold text-sm leading-tight truncate">
                    {organization?.name}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    Maintenance
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={toggleSidebar}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="w-full"
              onClick={toggleSidebar}
            >
              <div className="rounded w-full flex items-center justify-center text-primary-foreground font-semibold text-xs">
                <img src={logo} className='w-13' alt="" />
              </div>
            </Button>
          )}
        </div>

        <MaintenanceSidebarNav
          topActions={filteredTopActions}
          filteredNavigation={filteredNavigation}
          pathname={pathname}
          sidebarCollapsed={sidebarCollapsed}
          onSearchClick={handleSearchClick}
        />
      </aside>
    </TooltipProvider>
  );
}
